import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Info, AlertTriangle, Clock } from "lucide-react";
import api from "../lib/api";

const STATUS_STYLE = {
  urgent: { badge: "bg-red-500/15 text-red-400 border-red-500/30", label: "URGENT" },
  warning: { badge: "bg-amber-500/15 text-amber-400 border-amber-500/30", label: "UPCOMING" },
  normal: { badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "ON TRACK" },
};

export default function DeadlineTracker() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/deadlines").then((res) => setItems(res.data)).catch(() => {});
  }, []);

  const fixed = items.filter((i) => i.type === "fixed");
  const info = items.filter((i) => i.type === "info");

  const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <section id="deadlines" className="py-20 lg:py-28 bg-[#0B132B] relative grain-overlay" data-testid="deadline-tracker-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3">Must-Know Dates</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-none">
            Registration & Permit Deadlines
          </h2>
          <p className="text-slate-400 mt-4 text-sm sm:text-base leading-relaxed">
            These dates roll forward automatically every year — nothing here ever goes stale. Miss one, and your truck sits.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          {fixed.map((item) => {
            const s = STATUS_STYLE[item.status] || STATUS_STYLE.normal;
            return (
              <div key={item.id} data-testid={`deadline-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#131C38] border border-[#1E293B] rounded-lg p-6 hover:border-[#F97316]/50 transition-colors duration-300 group">
                <div className="flex items-start justify-between mb-4">
                  <span className={`text-[10px] font-mono2 tracking-widest px-2.5 py-1 rounded border ${s.badge}`}
                    data-testid="deadline-status-badge">{s.label}</span>
                  {item.status === "urgent"
                    ? <AlertTriangle className="w-5 h-5 text-red-400" />
                    : item.status === "warning"
                      ? <Clock className="w-5 h-5 text-amber-400" />
                      : <CalendarClock className="w-5 h-5 text-emerald-400" />}
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-1">{item.name}</h3>
                <div className="flex items-baseline gap-2 my-3">
                  <span className="font-mono2 text-4xl font-bold text-[#F97316] group-hover:scale-105 transition-transform origin-left"
                    data-testid="deadline-days-remaining">{item.days_remaining}</span>
                  <span className="text-xs text-slate-400 uppercase tracking-wider">days left</span>
                </div>
                <p className="font-mono2 text-xs text-slate-300 mb-3" data-testid="deadline-next-date">Next: {fmt(item.next_date)}</p>
                <p className="text-xs text-slate-400 leading-relaxed">{item.note}</p>
              </div>
            );
          })}
        </div>
        {info.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="deadline-info-items">
            {info.map((item) => (
              <div key={item.id} data-testid={`deadline-info-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="border border-dashed border-[#1E293B] rounded-lg p-5 flex gap-3">
                <Info className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-slate-200 mb-1">{item.name}</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{item.note}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-10 text-center">
          <Link to="/contact" data-testid="deadline-cta-btn"
            className="inline-block bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold text-sm px-8 py-3.5 rounded-md transition-colors">
            Never Miss a Date — Let Us Track Them
          </Link>
        </div>
      </div>
    </section>
  );
}
